/**
 * ContentProtection Component
 * Prevents copying/selecting text for non-admin users
 * Includes watermark overlay with user identification
 * Admins are excluded from this protection
 */
import { useEffect } from 'react';

const ContentProtection = ({ children, isAdmin = false, className = '', userEmail = '', userName = '' }) => {
  useEffect(() => {
    // Skip all protection for admins
    if (isAdmin) return;

    // Prevent context menu (right-click)
    const handleContextMenu = (e) => {
      // Allow on input/textarea elements
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      e.preventDefault();
    };

    // Prevent copy
    const handleCopy = (e) => {
      // Allow on input/textarea elements
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      e.preventDefault();
      // Optional: Show a message
    };

    // Prevent cut
    const handleCut = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      e.preventDefault();
    };

    // Prevent keyboard shortcuts (Ctrl+C, Ctrl+A, etc.)
    const handleKeyDown = (e) => {
      // Allow on input/textarea elements
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      // Block Ctrl+C, Ctrl+A, Ctrl+X, Ctrl+P
      if (e.ctrlKey || e.metaKey) {
        if (['c', 'a', 'x', 'p', 'u'].includes(e.key.toLowerCase())) {
          e.preventDefault();
        }
      }
      
      // Block Print Screen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('cut', handleCut);
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCut);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAdmin]);

  // CSS class for non-selectable text (admins excluded)
  const protectionClass = isAdmin ? '' : 'select-none';
  
  // Generate watermark identifier
  const watermarkText = userName || userEmail || 'User';
  
  // Create a pattern of the watermark text
  const generateWatermarkPattern = () => {
    if (isAdmin) return null;
    
    return (
      <div 
        className="absolute inset-0 pointer-events-none overflow-hidden z-10"
        aria-hidden="true"
      >
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `repeating-linear-gradient(
              -45deg,
              transparent,
              transparent 100px,
              rgba(0,0,0,0.02) 100px,
              rgba(0,0,0,0.02) 200px
            )`,
          }}
        />
        {/* Diagonal watermark pattern */}
        <div className="absolute inset-0 flex flex-wrap gap-32 -rotate-12 scale-150 opacity-[0.04]">
          {Array.from({ length: 50 }).map((_, i) => (
            <span 
              key={i} 
              className="text-slate-900 dark:text-white text-sm font-mono whitespace-nowrap"
              style={{ userSelect: 'none' }}
            >
              {watermarkText}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={`${protectionClass} ${className} relative`}>
      {generateWatermarkPattern()}
      <div className="relative z-0">
        {children}
      </div>
    </div>
  );
};

export default ContentProtection;
